import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "webhookCount"];
  static values = {
    organizationId: String,
    baseUrl: String,
  };

  initialize() {
    // Extract organization ID from URL if not provided
    if (!this.organizationIdValue) {
      const pathParts = window.location.pathname.split("/");
      const orgIndex = pathParts.indexOf("organizations");
      if (orgIndex !== -1 && pathParts[orgIndex + 1]) {
        this.organizationIdValue = pathParts[orgIndex + 1];
      }
    }
  }

  connect() {
    this.baseUrlValue = this.baseUrlValue || window.location.origin;
    // Load webhook count when controller connects
    this.loadWebhookCount();
  }

  async showCreateForm(event) {
    const type = event.params.type;
    const url =
      type === "api-key"
        ? `${this.baseUrlValue}/organizations/${this.organizationIdValue}/api_keys/new`
        : `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints/new`;

    await this.loadFormInModal(url);
  }

  async showEditForm(event) {
    const type = event.params.type;
    const id = event.params.id;
    const url =
      type === "api-key"
        ? `${this.baseUrlValue}/organizations/${this.organizationIdValue}/api_keys/${id}/edit`
        : `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints/${id}/edit`;

    await this.loadFormInModal(url);
  }

  async showDetails(event) {
    const type = event.params.type;
    const id = event.params.id;
    const url =
      type === "api-key"
        ? `${this.baseUrlValue}/organizations/${this.organizationIdValue}/api_keys/${id}`
        : `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints/${id}`;

    await this.loadFormInModal(url);
  }

  async toggleStatus(event) {
    const type = event.params.type;
    const id = event.params.id;
    const currentStatus = event.params.status === "true";
    const action = currentStatus ? "deactivate" : "activate";

    if (
      !confirm(
        `Are you sure you want to ${action} this ${type.replace("-", " ")}?`,
      )
    ) {
      return;
    }

    try {
      const url =
        type === "api-key"
          ? `${this.baseUrlValue}/organizations/${this.organizationIdValue}/api_keys/${id}/deactivate`
          : `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints/${id}/toggle_active`;

      const response = await this.fetchWithCSRF(url, {
        method: "PATCH",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        this.refreshCurrentTab();
        this.showToast(
          `${type.replace("-", " ")} ${action}d successfully`,
          "success",
        );
      }
    } catch (error) {
      console.error("Toggle status error:", error);
      this.showToast(`Failed to ${action} ${type.replace("-", " ")}`, "error");
    }
  }

  async confirmDelete(event) {
    const type = event.params.type;
    const id = event.params.id;
    const name = event.params.name;

    if (
      !confirm(
        `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      const url =
        type === "api-key"
          ? `${this.baseUrlValue}/organizations/${this.organizationIdValue}/api_keys/${id}`
          : `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints/${id}`;

      const response = await this.fetchWithCSRF(url, {
        method: "DELETE",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
        },
      });

      if (response.ok) {
        this.refreshCurrentTab();
        this.showToast(
          `${type.replace("-", " ")} deleted successfully`,
          "success",
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
      this.showToast(`Failed to delete ${type.replace("-", " ")}`, "error");
    }
  }

  async testWebhook(event) {
    const id = event.params.id;

    try {
      const response = await this.fetchWithCSRF(
        `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints/${id}/test`,
        {
          method: "POST",
          headers: {
            Accept: "text/vnd.turbo-stream.html",
          },
        },
      );

      if (response.ok) {
        this.showToast("Test webhook queued for delivery", "success");
      }
    } catch (error) {
      console.error("Test webhook error:", error);
      this.showToast("Failed to send test webhook", "error");
    }
  }

  closeModal() {
    this.modalTarget.classList.add("hidden");
    // Clear the form frame
    const formFrame = document.getElementById("form_frame");
    if (formFrame) {
      formFrame.innerHTML = "";
    }
  }

  // Handle successful form submissions
  handleFormSuccess(event) {
    this.closeModal();
    this.refreshCurrentTab();

    // Show success message if provided
    const detail = event.detail;
    if (detail && detail.message) {
      this.showToast(detail.message, "success");
    }
  }

  // Handle form errors
  handleFormError(event) {
    // Form errors are handled inline in the modal
    // We don't close the modal on error
  }

  async loadFormInModal(url) {
    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html, text/html",
          "Turbo-Frame": "form_frame",
        },
      });

      if (response.ok) {
        const html = await response.text();
        const formFrame = document.getElementById("form_frame");
        if (formFrame) {
          formFrame.innerHTML = html;
          this.modalTarget.classList.remove("hidden");
        }
      } else {
        console.error(
          "Form load failed:",
          response.status,
          response.statusText,
        );
        this.showToast("Failed to load form", "error");
      }
    } catch (error) {
      console.error("Load form error:", error);
      this.showToast("Failed to load form", "error");
    }
  }

  async loadWebhookCount() {
    try {
      const response = await fetch(
        `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (this.hasWebhookCountTarget && data.webhook_count !== undefined) {
          this.webhookCountTarget.textContent = data.webhook_count;
        }
      }
    } catch (error) {
      console.error("Load webhook count error:", error);
    }
  }

  refreshCurrentTab() {
    // Find the active tab and refresh its content
    const activeTab = this.element.querySelector(
      '[data-tabs-target="tab"][class*="border-primary"]',
    );
    if (activeTab) {
      const tabName = activeTab.dataset.tab;
      this.refreshTabContent(tabName);
    }
  }

  refreshTabContent(tabName) {
    if (tabName === "api-keys") {
      // Refresh API keys list
      const frame = document.getElementById("api_keys_list");
      if (frame) {
        frame.src = `${this.baseUrlValue}/organizations/${this.organizationIdValue}/api_keys?format=turbo_stream`;
        frame.reload();
      }
    } else if (tabName === "webhooks") {
      // Refresh webhooks list
      const frame = document.getElementById("webhooks_content");
      if (frame) {
        frame.src = `${this.baseUrlValue}/organizations/${this.organizationIdValue}/webhook_endpoints?format=turbo_stream`;
        frame.reload();
      }
      this.loadWebhookCount();
    }
  }

  showToast(message, type = "info") {
    window.showToast(message, type);
  }

  // Event listeners for turbo events
  turboSubmitEnd(event) {
    if (event.detail.success) {
      this.handleFormSuccess(event);
    } else {
      this.handleFormError(event);
    }
  }

  // Helper method to fetch with CSRF token
  async fetchWithCSRF(url, options = {}) {
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;

    const defaultOptions = {
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
        ...options.headers,
      },
    };

    return fetch(url, { ...defaultOptions, ...options });
  }
}
